/**
 * Map — `/map`. Interactive coverage map for the Omaha launch metro.
 *
 * Privacy invariants (from /app/memory/map_step0_investigation.md §2):
 *   1. ZIP-polygon or larger only. No street-level pins.
 *   2. No individual addresses (customer / provider / booking).
 *   3. No real-time activity. No provider counts per ZIP in tooltips.
 *   4. No admin "show me addresses" override.
 *
 * Real-partner-only invariant (2026-06-22 Map addendum):
 *   - The map shades ZIPs covered by REAL partners ONLY. The seeded
 *     in-house "Plucky's Crew" (is_pooplucker_default=true) is
 *     EXCLUDED from /api/public/coverage-zips + /api/public/providers-in-zip.
 *   - When no real partners exist (current state), the map collapses
 *     to a friendly empty state with a recruiter CTA + waitlist form.
 *
 * Data sources:
 *   - Polygons: static GeoJSON at `/coverage-omaha.geojson`.
 *   - Served set + `has_coverage` flag: `GET /api/public/coverage-zips`.
 *   - Side-panel providers: `GET /api/public/providers-in-zip?zip=XXXXX`
 *     (returns business_name + tier + per_dog_price + id).
 *   - Waitlist intake: `POST /api/public/coverage-notify`.
 *
 * Token: `REACT_APP_MAPBOX_TOKEN`. If missing, render a friendly
 * placeholder (separate fallback path from the empty state).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight, Star, CheckCircle2, Sparkles } from "lucide-react";
import "mapbox-gl/dist/mapbox-gl.css";
import { useTheme } from "@/theme/ThemeProvider";
import PublicPageShell from "@/components/PublicPageShell";
import PoliteStatus from "@/components/a11y/PoliteStatus";
import WinnerSlotCard from "@/components/WinnerSlotCard";
import ZipSearchBox from "@/components/map/ZipSearchBox";
import MetroChipRow from "@/components/map/MetroChipRow";
import AdminDensityOverlay, {
  BIN_COLORS as DENSITY_BIN_COLORS,
} from "@/components/map/AdminDensityOverlay";
import MapBottomSheet from "@/components/map/MapBottomSheet";
import { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { trackRecruitEvent } from "@/lib/recruitTracking";
import WaitlistDemandOverlay from "@/components/WaitlistDemandOverlay";

/* ---------------------------------------------------------------------------
   Helpers
   --------------------------------------------------------------------------- */

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";

/**
 * Phase L.1 — Mapbox GL StyleSpec layer paint constants.
 *
 * These values are consumed inside `map.addLayer({...paint})` blocks
 * where Mapbox reads them as literal color strings — it does NOT
 * resolve CSS custom properties (`var(--pp-*)`). To make Mapbox
 * layers truly theme-flippable requires `setPaintProperty` calls in
 * a useEffect gated on `theme.effective` — deferred beyond L.1 (see
 * "Next Action Items" in L.1 report-back).
 *
 * Values chosen to match brand-constant tokens (orange + orangeHover
 * are identical across themes per Phase L). The muted-fill uses a
 * neutral slate that reads acceptably against both light + dark
 * base tilesets.
 */
const MAP_LAYER_PAINT = {
  brandOrange: "#F89623",      // matches --pp-orange (brand-constant)
  brandOrangeHover: "#EA8815", // matches --pp-orange-hover (brand-constant)
  mutedSlate: "#9AA4AC",       // reads OK against both light + dark tilesets
};

function useEffectiveDark() {
  // Phase L.1 — Migrated from `matchMedia("(prefers-color-scheme: dark)")`
  // to the app's ThemeProvider. Phase L.1.1 renamed from
  // `usePrefersDark` to `useEffectiveDark` since the semantic is now
  // `theme.effective === "dark"` — the OS preference is intentionally
  // ignored per user's Family L invariant. See ThemeProvider docs for
  // the auto-vs-pinned rules that determine effective.
  const { effective } = useTheme();
  return effective === "dark";
}

function tierBadge(tier) {
  if (!tier) return null;
  const label = tier.badge_label || tier.name || "Partner";
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2.5 py-1"
      style={{
        background: "var(--pp-lavender)",
        color: "var(--pp-lavender-text)",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
      }}
      data-testid="provider-tier-badge"
    >
      <Star size={11} strokeWidth={2.5} aria-hidden />
      {label}
    </span>
  );
}

function fmtPrice(n) {
  if (n == null || Number.isNaN(Number(n))) return null;
  const v = Number(n);
  return v % 1 === 0 ? `$${v.toFixed(0)}` : `$${v.toFixed(2)}`;
}

/* ---------------------------------------------------------------------------
   Empty state — when no real partners exist anywhere yet. Replaces the
   map canvas + side panel with a recruiter CTA + waitlist form.
   --------------------------------------------------------------------------- */
function EmptyStatePanel() {
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  // F.4 — funnel event: empty state viewed (fires once on mount).
  useEffect(() => {
    trackRecruitEvent("empty_state_viewed", { source: "map_empty" });
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Please enter your email.");
      return;
    }
    const zipTrim = zip.trim();
    if (zipTrim && !/^\d{5}$/.test(zipTrim)) {
      setError("ZIP must be 5 digits or left blank.");
      return;
    }
    setSubmitting(true);
    try {
      const body = { email: email.trim() };
      if (zipTrim) body.zip = zipTrim;
      const r = await fetch(`${API_BASE}/public/coverage-notify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        let detail = "Couldn't add you right now. Please try again in a moment.";
        try {
          const j = await r.json();
          detail = formatApiErrorDetail(j?.detail) || detail;
        } catch {
          /* ignore parse errors */
        }
        throw new Error(detail);
      }
      setSubmitted(true);
      // F.4 — funnel event: empty-state waitlist submitted.
      trackRecruitEvent("empty_state_waitlist_submitted", {
        zip: zipTrim || undefined,
        source: "map_empty",
      });
    } catch (e2) {
      setError(e2.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="rounded-[28px] p-10 lg:p-14"
      style={{
        background: "var(--pp-lavender)",
        color: "var(--pp-lavender-text)",
        boxShadow: "var(--pp-shadow)",
      }}
      data-testid="map-empty-state"
    >
      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 items-start">
        {/* LEFT: Recruiter pitch */}
        <div>
          <div className="pp-kick" style={{ fontSize: 12 }}>
            Coverage coming soon
          </div>
          <h2
            className="pp-h2 mt-3"
            style={{ fontSize: 32, letterSpacing: "-0.02em", color: "inherit" }}
            data-testid="map-empty-h2"
          >
            Be the first PooPartner in your area.
          </h2>
          <p
            className="mt-4"
            style={{
              fontSize: 16,
              lineHeight: 1.55,
              color: "inherit",
              opacity: 0.92,
              maxWidth: "32em",
            }}
          >
            We&rsquo;re recruiting verified PooPartners in your area and surrounding
            communities. Sign up to get notified when service launches in your
            neighborhood — or apply to become a partner and help us launch
            faster.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Link
              to="/signup?role=provider"
              onClick={() => trackRecruitEvent("empty_state_partner_clicked", {
                source: "map_empty",
              })}
              className="pp-btn pp-btn-primary"
              data-testid="map-empty-cta-partner"
            >
              Become a PooPartner
              <ArrowRight className="arrow" size={14} aria-hidden />
            </Link>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                trackRecruitEvent("empty_state_notify_clicked", {
                  source: "map_empty",
                });
                const el = document.getElementById("waitlist");
                if (el) {
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
                  // Preserve URL hash so deep-links still work; replaceState
                  // avoids cluttering browser history with hash steps.
                  window.history.replaceState(null, "", "#waitlist");
                }
              }}
              className="pp-btn pp-btn-ghost"
              style={{ background: "transparent", borderColor: "currentColor" }}
              data-testid="map-empty-cta-notify-anchor"
            >
              Notify me when available
            </button>
          </div>
        </div>

        {/* RIGHT: Waitlist form */}
        <div
          id="waitlist"
          className="rounded-[20px] p-6"
          style={{
            background: "var(--pp-v3-bg)",
            color: "var(--pp-slate)",
            border: "1px solid var(--pp-line)",
          }}
        >
          {submitted ? (
            <div data-testid="map-empty-waitlist-confirmation">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2
                  size={20}
                  style={{ color: "var(--pp-orange)" }}
                  aria-hidden
                />
                <h3 className="pp-h2" style={{ fontSize: 18, margin: 0 }}>
                  You&rsquo;re on the list.
                </h3>
              </div>
              <p className="pp-sm" style={{ color: "var(--pp-text-soft)" }}>
                We&rsquo;ll email you the moment we launch nearby. In the meantime,
                tell a dog-loving friend in your area.
              </p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="map-empty-waitlist-form"
              noValidate
            >
              <div className="flex items-center gap-2 mb-2">
                <Sparkles
                  size={18}
                  style={{ color: "var(--pp-orange)" }}
                  aria-hidden
                />
                <h3 className="pp-h2" style={{ fontSize: 18, margin: 0 }}>
                  Notify me when available
                </h3>
              </div>
              <p
                className="pp-sm mb-4"
                style={{ color: "var(--pp-text-soft)" }}
              >
                We&rsquo;ll send one email when we go live near you. No
                marketing spam.
              </p>
              <label
                htmlFor="waitlist-email"
                className="block mb-2"
                style={{ fontSize: 13, fontWeight: 600 }}
              >
                Email
              </label>
              <input
                id="waitlist-email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-[10px] px-3 py-2.5 mb-4"
                style={{
                  background: "var(--pp-v3-surface)",
                  border: "1px solid var(--pp-line)",
                  color: "var(--pp-slate)",
                  fontSize: 14,
                }}
                data-testid="map-empty-waitlist-email"
              />
              <label
                htmlFor="waitlist-zip"
                className="block mb-2"
                style={{ fontSize: 13, fontWeight: 600 }}
              >
                ZIP <span style={{ color: "var(--pp-text-soft)" }}>(optional)</span>
              </label>
              <input
                id="waitlist-zip"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{5}"
                maxLength={5}
                value={zip}
                onChange={(e) => setZip(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="68022"
                className="w-full rounded-[10px] px-3 py-2.5 mb-4"
                style={{
                  background: "var(--pp-v3-surface)",
                  border: "1px solid var(--pp-line)",
                  color: "var(--pp-slate)",
                  fontSize: 14,
                  maxWidth: 160,
                }}
                data-testid="map-empty-waitlist-zip"
              />
              {error && (
                <p
                  className="pp-sm mb-3 text-red-700"
                  data-testid="map-empty-waitlist-error"
                >
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="pp-btn pp-btn-primary w-full justify-center"
                style={{ opacity: submitting ? 0.7 : 1 }}
                data-testid="map-empty-waitlist-submit"
              >
                {submitting ? "Submitting…" : "Notify me"}
                {!submitting && (
                  <ArrowRight className="arrow" size={14} aria-hidden />
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Side panel — provider list for the active ZIP (Map populated state).
   --------------------------------------------------------------------------- */
function ZipSidePanel({ activeZip, providers, loading, error, onClose }) {
  if (!activeZip) {
    return (
      <div
        className="rounded-[24px] p-6 h-full flex flex-col"
        style={{
          background: "var(--pp-v3-surface)",
          border: "1px solid var(--pp-line)",
        }}
        data-testid="map-side-panel-empty"
      >
        <div className="flex items-center gap-2 mb-3">
          <MapPin size={18} style={{ color: "var(--pp-orange)" }} aria-hidden />
          <h3
            className="pp-h2"
            style={{ fontSize: 18, letterSpacing: "-0.01em", margin: 0 }}
          >
            Pick a ZIP
          </h3>
        </div>
        <p className="pp-sm" style={{ color: "var(--pp-text-soft)" }}>
          Tap any orange ZIP on the map to see the PooPartners covering it.
          Gray ZIPs aren&rsquo;t live yet — we&rsquo;re expanding metro-by-metro.
        </p>
        <ul
          className="mt-5 space-y-2"
          style={{ color: "var(--pp-text-soft)", fontSize: 13.5 }}
        >
          <li>· Orange = currently served</li>
          <li>· Gray = not yet served</li>
          <li>· Click an orange ZIP for partners</li>
        </ul>
      </div>
    );
  }

  return (
    <div
      className="rounded-[24px] p-6 h-full flex flex-col"
      style={{
        background: "var(--pp-v3-surface)",
        border: "1px solid var(--pp-line)",
      }}
      data-testid="map-side-panel-active"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="pp-kick" style={{ fontSize: 12 }}>
            ZIP {activeZip}
          </div>
          <h3
            className="pp-h2 mt-1"
            style={{ fontSize: 20, letterSpacing: "-0.01em", margin: 0 }}
            data-testid="map-side-panel-heading"
          >
            Plucking this area
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close ZIP details"
          className="rounded-full w-8 h-8 inline-flex items-center justify-center"
          style={{
            color: "var(--pp-text-soft)",
            border: "1px solid var(--pp-line)",
            background: "var(--pp-v3-bg)",
          }}
          data-testid="map-side-panel-close"
        >
          ×
        </button>
      </div>

      {loading && (
        <p
          className="pp-sm"
          style={{ color: "var(--pp-text-soft)" }}
          data-testid="map-side-panel-loading"
          role="status"
          aria-live="polite"
        >
          Loading PooPartners…
        </p>
      )}

      {error && (
        <p
          className="pp-sm text-red-700"
          data-testid="map-side-panel-error"
        >
          {error}
        </p>
      )}

      {!loading && !error && providers.length === 0 && (
        <p
          className="pp-sm"
          style={{ color: "var(--pp-text-soft)" }}
          data-testid="map-side-panel-empty-providers"
        >
          No PooPartners covering this ZIP yet. Try a neighboring orange ZIP.
        </p>
      )}

      {!loading && !error && providers.length > 0 && (
        <ul className="space-y-3" data-testid="map-side-panel-provider-list">
          {providers.map((p) => {
            const price = fmtPrice(p.per_dog_price);
            return (
              <li
                key={p.id}
                className="rounded-[16px] p-4"
                style={{
                  background: "var(--pp-v3-bg)",
                  border: "1px solid var(--pp-line)",
                }}
                data-testid={`map-provider-card-${p.id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="font-semibold" style={{ fontSize: 15 }}>
                    {p.business_name}
                  </div>
                  {price && (
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 14,
                        color: "var(--pp-slate)",
                      }}
                      data-testid={`map-provider-price-${p.id}`}
                    >
                      {price}
                      <span
                        style={{
                          color: "var(--pp-text-soft)",
                          fontWeight: 500,
                          fontSize: 12,
                        }}
                      >
                        {" / dog"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="mb-3">{tierBadge(p.tier)}</div>
                <Link
                  to={`/book?provider_id=${encodeURIComponent(p.id)}`}
                  className="pp-btn pp-btn-ghost w-full justify-center"
                  data-testid={`map-provider-book-cta-${p.id}`}
                >
                  Book with this partner
                  <ArrowRight size={14} aria-hidden />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Mapbox-backed canvas.
   --------------------------------------------------------------------------- */
function MapCanvas({ servedZips, geojson, coverage, mapRef, onZipClick, density }) {
  const containerRef = useRef(null);
  // `mapRef` is lifted to the outer `Map` component so <MetroChipRow>
  // + <MapCanvas> can share the same Mapbox instance handle. The
  // multi-metro readiness dispatch accidentally moved this useRef
  // INSIDE MapCanvas, which caused the outer <MetroChipRow mapRef={mapRef} />
  // to hit `ReferenceError: mapRef is not defined` and crash /map in
  // production. Root-cause guard: reproduction tests in
  // `Map.emptydata.test.jsx` scenario 6.
  const isDark = useEffectiveDark();
  const styleUrl = isDark
    ? "mapbox://styles/mapbox/dark-v11"
    : "mapbox://styles/mapbox/light-v11";

  // Phase F.3 — density map: zip -> { bin, count }. Built once per
  // density-prop change and joined onto the GeoJSON features below.
  // When `density` is null, the lookup is empty and no features
  // receive a density_bin — the density layer renders fully empty.
  // NOTE: plain object (not `new Map()`) because this file exports a
  // React component named `Map` which shadows the global Map class.
  const densityLookup = useMemo(() => {
    const out = {};
    if (!density || !Array.isArray(density.zips)) return out;
    for (const row of density.zips) {
      if (row && row.zip) {
        out[String(row.zip)] = { bin: row.bin, count: row.booking_count };
      }
    }
    return out;
  }, [density]);

  // Centroid data for the B.1 fallback (Coverage-Map multi-metro
  // readiness). When the active metro has NO per-metro polygon file
  // (`coverage.geojson_path === null` — KC / DFW / Phoenix / Denver
  // launch), fetch the ZCTA centroid for each covered ZIP and render
  // it as a small circle marker instead of a colored polygon. Omaha
  // stays on polygons unchanged.
  const [centroids, setCentroids] = useState(null);

  useEffect(() => {
    if (!coverage || coverage.geojson_path) {
      // Polygon-metro (Omaha) or coverage not loaded yet — skip.
      return;
    }
    const zips = coverage.coverage_zips || [];
    if (zips.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        // Endpoint caps at 300 ZIPs; our metros stay well under this.
        const r = await fetch(
          `${API_BASE}/public/zcta-centroids?zips=${zips.join(",")}`
        );
        if (!r.ok) throw new Error("centroid fetch failed");
        const data = await r.json();
        if (!cancelled) setCentroids(data.centroids || {});
      } catch (_e) {
        if (!cancelled) setCentroids({});
      }
    })();
    return () => { cancelled = true; };
  }, [coverage]);

  const annotated = useMemo(() => {
    if (geojson && servedZips) {
      // Polygon path (unchanged behavior for Omaha).
      const set = new Set(servedZips);
      return {
        ...geojson,
        features: (geojson.features || []).map((f) => {
          const zip = String(f.properties?.zip || "");
          return {
            ...f,
            properties: {
              ...(f.properties || {}),
              served: set.has(zip),
            },
          };
        }),
      };
    }
    // Centroid-marker fallback path (B.1). Synthesize a Point
    // FeatureCollection from the centroid lookup. `served` mirrors
    // the polygon path's semantic — ZIP is in the served-ZIPs set
    // computed from provider service_zips ∩ metro coverage_zips.
    if (centroids && servedZips) {
      const set = new Set(servedZips);
      const zipsForMetro = Object.keys(centroids);
      const features = zipsForMetro.map((zip) => ({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [centroids[zip].lng, centroids[zip].lat],
        },
        properties: {
          zip,
          served: set.has(zip),
        },
      }));
      return { type: "FeatureCollection", features };
    }
    return null;
  }, [geojson, centroids, servedZips]);

  // F.3 — Build a SEPARATE GeoJSON feature collection containing only
  // ZIPs with a density_bin assigned. This lives in its own Mapbox
  // source so density toggles update only the overlay source — the
  // base map source (and the user's zoom/pan state) stay intact.
  const densityFeatureCollection = useMemo(() => {
    const keys = Object.keys(densityLookup);
    if (!geojson || keys.length === 0) {
      return { type: "FeatureCollection", features: [] };
    }
    const out = [];
    for (const f of geojson.features || []) {
      const zip = String(f.properties?.zip || "");
      const d = densityLookup[zip];
      if (!d) continue;
      out.push({
        ...f,
        properties: {
          ...(f.properties || {}),
          zip,
          density_bin:   d.bin,
          density_count: d.count,
        },
      });
    }
    return { type: "FeatureCollection", features: out };
  }, [geojson, densityLookup]);

  const bounds = useMemo(() => {
    if (!annotated) return null;
    const features = annotated.features.filter((f) => f.properties.served);
    const target = features.length ? features : annotated.features;
    // Missed-Items Prod Hotfix Batch 1 P0-1 — legacy / empty data
    // safety: `target` can be empty if BOTH the served-filter AND
    // the parent feature array come back with zero rows (unseeded
    // metro, coverage transition state). Bail early so fit-to-bounds
    // doesn't get called with an invalid Infinity/-Infinity bbox
    // downstream.
    if (target.length === 0) return null;
    let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
    // Handle both polygon (Polygon / MultiPolygon rings) and centroid
    // (Point coordinates) geometries — the multi-metro launch layers
    // Point features when a metro has no per-metro polygon file.
    const _extend = (lng, lat) => {
      if (typeof lng !== "number" || typeof lat !== "number") return;
      if (lng < minLng) minLng = lng;
      if (lat < minLat) minLat = lat;
      if (lng > maxLng) maxLng = lng;
      if (lat > maxLat) maxLat = lat;
    };
    target.forEach((f) => {
      const gt = f.geometry?.type;
      if (gt === "Point") {
        const [lng, lat] = f.geometry?.coordinates || [];
        _extend(lng, lat);
      } else {
        // Polygon: coords[0] is the outer ring; MultiPolygon: coords[i][0].
        const rings = gt === "MultiPolygon"
          ? (f.geometry?.coordinates || []).map((p) => p[0])
          : [f.geometry?.coordinates?.[0] || []];
        rings.forEach((ring) => ring.forEach(([lng, lat]) => _extend(lng, lat)));
      }
    });
    if (!isFinite(minLng)) return null;
    return [[minLng, minLat], [maxLng, maxLat]];
  }, [annotated]);

  useEffect(() => {
    if (!MAPBOX_TOKEN || !containerRef.current || mapRef.current || !annotated) return;
    let cancelled = false;
    let mapInstance = null;
    (async () => {
      const mod = await import("mapbox-gl");
      const mapboxgl = mod.default || mod;
      mapboxgl.accessToken = MAPBOX_TOKEN;
      if (cancelled) return;
      // Init center: consume API-provided `coverage.primary_center`
      // (per-metro from the metros collection). When coverage is
      // still loading or the collection is empty, fall back to a
      // continental-US default. NO hardcoded metro-specific coords
      // — a Phoenix-only DB seeds the map at Phoenix, not Omaha.
      const pc = coverage?.primary_center;
      const initCenter = pc && typeof pc.lng === "number" && typeof pc.lat === "number"
        ? [pc.lng, pc.lat]
        : [-98.5, 39.8];  // continental US visual center
      const initZoom = pc && typeof pc.zoom === "number" ? pc.zoom : 3.5;
      mapInstance = new mapboxgl.Map({
        container: containerRef.current,
        style: styleUrl,
        center: initCenter,
        zoom: initZoom,
        attributionControl: true,
        cooperativeGestures: true,
      });
      mapInstance.addControl(
        new mapboxgl.NavigationControl({ showCompass: false }),
        "top-right"
      );
      mapInstance.on("load", () => {
        if (cancelled) return;
        mapInstance.addSource("coverage", { type: "geojson", data: annotated });
        // Polygon layers — no-op for centroid-marker (Point-only)
        // datasets since fill/line paints skip non-polygon features.
        mapInstance.addLayer({
          id: "coverage-fill",
          type: "fill",
          source: "coverage",
          filter: ["==", ["geometry-type"], "Polygon"],
          paint: {
            "fill-color": [
              "case",
              ["==", ["get", "served"], true],
              MAP_LAYER_PAINT.brandOrange,
              MAP_LAYER_PAINT.mutedSlate,
            ],
            "fill-opacity": [
              "case",
              ["==", ["get", "served"], true],
              0.45,
              0.12,
            ],
          },
        });
        mapInstance.addLayer({
          id: "coverage-line",
          type: "line",
          source: "coverage",
          filter: ["==", ["geometry-type"], "Polygon"],
          paint: {
            "line-color": [
              "case",
              ["==", ["get", "served"], true],
              MAP_LAYER_PAINT.brandOrangeHover,
              "rgba(105, 118, 128, 0.4)",
            ],
            "line-width": [
              "case",
              ["==", ["get", "served"], true],
              1.5,
              0.6,
            ],
          },
        });
        // Centroid-marker fallback layer (Coverage-Map multi-metro
        // readiness — B.1). Fires only for Point features (the
        // synthesized centroid FeatureCollection for KC / DFW /
        // Phoenix / Denver launch metros). Uses the SAME served /
        // unserved color pair as polygons so the map palette is
        // consistent across polygon + centroid-marker metros.
        mapInstance.addLayer({
          id: "coverage-centroid-marker",
          type: "circle",
          source: "coverage",
          filter: ["==", ["geometry-type"], "Point"],
          paint: {
            "circle-radius": [
              "interpolate", ["linear"], ["zoom"],
              6,  3,
              10, 8,
              14, 14,
            ],
            "circle-color": [
              "case",
              ["==", ["get", "served"], true],
              MAP_LAYER_PAINT.brandOrange,
              MAP_LAYER_PAINT.mutedSlate,
            ],
            "circle-opacity": [
              "case",
              ["==", ["get", "served"], true],
              0.75,
              0.35,
            ],
            "circle-stroke-color": MAP_LAYER_PAINT.brandOrangeHover,
            "circle-stroke-width": [
              "case",
              ["==", ["get", "served"], true],
              1.5,
              0.5,
            ],
          },
        });
        // F.3 — admin density overlay source + layer.
        // SEPARATE source from `coverage` so density toggles don't
        // disturb the base map source (and the user's zoom/pan state).
        // The source starts empty; the dedicated update-effect below
        // calls setData on density-prop changes.
        mapInstance.addSource("coverage-density", {
          type: "geojson",
          data: densityFeatureCollection,
        });
        mapInstance.addLayer({
          id: "coverage-density-fill",
          type: "fill",
          source: "coverage-density",
          paint: {
            "fill-color": [
              "match",
              ["get", "density_bin"],
              "low",  DENSITY_BIN_COLORS.low,
              "mid",  DENSITY_BIN_COLORS.mid,
              "high", DENSITY_BIN_COLORS.high,
              "rgba(0,0,0,0)",
            ],
            "fill-opacity": [
              "match",
              ["get", "density_bin"],
              "low",  0.55,
              "mid",  0.65,
              "high", 0.75,
              0,
            ],
          },
        });
        mapInstance.on("click", "coverage-fill", (e) => {
          const f = e.features?.[0];
          if (!f) return;
          const zip = String(f.properties?.zip || "");
          const isServed =
            f.properties?.served === true || f.properties?.served === "true";
          if (!zip) return;
          onZipClick({ zip, served: isServed });
          try {
            const ring = f.geometry?.coordinates?.[0] || [];
            if (ring.length) {
              let mnLng = Infinity, mnLat = Infinity, mxLng = -Infinity, mxLat = -Infinity;
              ring.forEach(([lng, lat]) => {
                if (lng < mnLng) mnLng = lng;
                if (lat < mnLat) mnLat = lat;
                if (lng > mxLng) mxLng = lng;
                if (lat > mxLat) mxLat = lat;
              });
              mapInstance.fitBounds(
                [[mnLng, mnLat], [mxLng, mxLat]],
                { padding: 80, maxZoom: 13, duration: 700 }
              );
            }
          } catch {
            /* swallow */
          }
        });
        mapInstance.on("mouseenter", "coverage-fill", () => {
          mapInstance.getCanvas().style.cursor = "pointer";
        });
        mapInstance.on("mouseleave", "coverage-fill", () => {
          mapInstance.getCanvas().style.cursor = "";
        });
        if (bounds) {
          mapInstance.fitBounds(bounds, { padding: 40, duration: 0 });
        }
      });
      mapRef.current = mapInstance;
    })();
    return () => {
      cancelled = true;
      if (mapInstance) mapInstance.remove();
      mapRef.current = null;
    };
    // Intentionally re-init only when the annotated dataset changes; style
    // swaps + click-handler ref refresh happen in sibling effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotated]);

  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    try {
      m.setStyle(styleUrl);
      m.once("style.load", () => {
        if (!m.getSource("coverage") && annotated) {
          m.addSource("coverage", { type: "geojson", data: annotated });
          m.addLayer({
            id: "coverage-fill",
            type: "fill",
            source: "coverage",
            paint: {
              "fill-color": [
                "case",
                ["==", ["get", "served"], true],
                MAP_LAYER_PAINT.brandOrange,
                MAP_LAYER_PAINT.mutedSlate,
              ],
              "fill-opacity": [
                "case",
                ["==", ["get", "served"], true],
                0.45,
                0.12,
              ],
            },
          });
          m.addLayer({
            id: "coverage-line",
            type: "line",
            source: "coverage",
            paint: {
              "line-color": [
                "case",
                ["==", ["get", "served"], true],
                MAP_LAYER_PAINT.brandOrangeHover,
                "rgba(105, 118, 128, 0.4)",
              ],
              "line-width": [
                "case",
                ["==", ["get", "served"], true],
                1.5,
                0.6,
              ],
            },
          });
          // F.3 — density layer reattach on style swap.
          m.addSource("coverage-density", {
            type: "geojson",
            data: densityFeatureCollection,
          });
          m.addLayer({
            id: "coverage-density-fill",
            type: "fill",
            source: "coverage-density",
            paint: {
              "fill-color": [
                "match",
                ["get", "density_bin"],
                "low",  DENSITY_BIN_COLORS.low,
                "mid",  DENSITY_BIN_COLORS.mid,
                "high", DENSITY_BIN_COLORS.high,
                "rgba(0,0,0,0)",
              ],
              "fill-opacity": [
                "match",
                ["get", "density_bin"],
                "low",  0.55,
                "mid",  0.65,
                "high", 0.75,
                0,
              ],
            },
          });
        }
      });
    } catch {
      /* best-effort */
    }
  }, [styleUrl, annotated, densityFeatureCollection]);

  // F.3 — Push the latest density feature collection into the
  // dedicated `coverage-density` source whenever density toggles or
  // the underlying ZIPs change. Base map source untouched.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    try {
      const src = m.getSource("coverage-density");
      if (src && typeof src.setData === "function") {
        src.setData(densityFeatureCollection);
      }
    } catch {
      /* best-effort */
    }
  }, [densityFeatureCollection]);

  if (!MAPBOX_TOKEN) {
    // Item 8b (Feb 2026 launch-blocking batch) — public fallback
    // safety. The previous fallback message leaked internal config
    // (`REACT_APP_MAPBOX_TOKEN`) and file paths (`/app/frontend/.env`)
    // directly to end users on the public `/map` route. The
    // replacement below (a) uses ONLY user-friendly language,
    // (b) leans on the existing ZIP search + coverage list which
    // render regardless of token state (the outer `Map()` page
    // continues to render those siblings), (c) uses Family-L tokens
    // exclusively. Drift-guard:
    // `frontend/src/pages/Map.publicFallbackSafety.test.jsx`.
    return (
      <div
        className="rounded-[28px] flex flex-col items-center justify-center text-center px-6"
        style={{
          minHeight: "60vh",
          background: "var(--pp-v3-surface)",
          border: "2px dashed var(--pp-line)",
        }}
        data-testid="map-token-missing-placeholder"
      >
        <MapPin
          size={36}
          style={{ color: "var(--pp-orange)", marginBottom: 12 }}
          aria-hidden
        />
        <h3
          className="pp-h2"
          style={{ fontSize: 22, letterSpacing: "-0.01em", margin: 0 }}
        >
          Map temporarily unavailable.
        </h3>
        <p className="pp-sm mt-3" style={{ maxWidth: "30em" }}>
          Search your ZIP below to check coverage in your area.
        </p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="rounded-[28px] overflow-hidden"
      style={{
        minHeight: "60vh",
        height: "60vh",
        boxShadow: "var(--pp-shadow)",
        border: "1px solid var(--pp-line)",
      }}
      data-testid="map-canvas"
      role="application"
      aria-label="Interactive coverage map. Use the ZIP search box above to look up coverage for a specific ZIP code."
      aria-describedby="map-keyboard-alt-hint"
    />
  );
}

/* ---------------------------------------------------------------------------
   Page
   --------------------------------------------------------------------------- */
export default function Map() {
  const [coverage, setCoverage] = useState(null);
  const [geojson, setGeojson] = useState(null);
  const [bootstrapError, setBootstrapError] = useState("");

  // Lifted to the outer `Map` scope (Missed-Items Prod Hotfix Batch 1
  // P0-1 fix). Shared between <MapCanvas> and <MetroChipRow>. Before
  // this lift the useRef sat inside MapCanvas, and the JSX below
  // referenced an undefined `mapRef` at render time → ReferenceError
  // → /map error-boundary in production.
  const mapRef = useRef(null);

  const [activeZip, setActiveZip] = useState(null);
  const [providers, setProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [providersError, setProvidersError] = useState("");

  // F.3 — admin density overlay payload (set by AdminDensityOverlay
  // when an admin toggles density ON). null when off / non-admin.
  const [density, setDensity] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Coverage payload carries `geojson_path` per metro. When null
        // (multi-metro launch: KC / DFW / Phoenix / Denver ship with
        // no per-metro polygon file yet — B.1 centroid-marker fallback
        // is the launch UX), skip the polygon fetch and let the
        // downstream centroid-marker layer render circles at each
        // ZIP's centroid instead. See `MetroCentroidMarkerLayer` below.
        const covRes = await fetch(`${API_BASE}/public/coverage-zips`);
        if (!covRes.ok) throw new Error("Couldn't load coverage list.");
        const cov = await covRes.json();
        if (cancelled) return;
        setCoverage(cov);
        // No polygon file registered for this metro → skip GeoJSON
        // fetch. `geojson` stays null; the map will render coverage
        // as centroid-markers via the fallback layer.
        if (!cov.geojson_path) {
          setGeojson(null);
          return;
        }
        const geoRes = await fetch(cov.geojson_path);
        if (!geoRes.ok) throw new Error("Couldn't load coverage polygons.");
        const geo = await geoRes.json();
        if (!cancelled) setGeojson(geo);
      } catch (e) {
        if (!cancelled)
          setBootstrapError(e.message || "Failed to load map data.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!activeZip) {
      setProviders([]);
      setProvidersError("");
      return;
    }
    let cancelled = false;
    setProvidersLoading(true);
    setProvidersError("");
    (async () => {
      try {
        const r = await fetch(
          `${API_BASE}/public/providers-in-zip?zip=${encodeURIComponent(activeZip)}`
        );
        if (!r.ok) throw new Error("Couldn't load PooPartners for this ZIP.");
        const data = await r.json();
        if (!cancelled) setProviders(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) {
          setProvidersError(e.message || "Couldn't load PooPartners.");
          setProviders([]);
        }
      } finally {
        if (!cancelled) setProvidersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeZip]);

  const handleZipClick = ({ zip, served }) => {
    if (served) setActiveZip(zip);
    else setActiveZip(null);
  };

  const hasCoverage = coverage?.has_coverage === true;
  const totalZips = coverage?.coverage_zips?.length ?? 0;
  const totalCities = coverage?.cities?.length ?? 0;
  const stillLoading = coverage === null && !bootstrapError;

  return (
    <PublicPageShell>
      {/* HERO */}
      <section className="pp-wrap" style={{ paddingTop: 56, paddingBottom: 24 }}>
        <div className="pp-eyebrow" data-testid="map-eyebrow">
          <span className="dot" />
          Service coverage
        </div>
        <h1
          className="pp-h1 mt-4"
          style={{ maxWidth: "16em" }}
          data-testid="map-hero-h1"
        >
          Where we <span className="acc">Pluck</span>.
        </h1>
        <p className="pp-sub mt-4">
          We&rsquo;re launching neighborhood-by-neighborhood in your area
          — and we never show individual addresses on this map. Only ZIPs
          covered by verified PooPartners are shaded.
        </p>
        {/* F.2 — ZIP search box. Visible when bootstrap completes. Privacy:
            5-digit ZIP input only, no address fields, no fetch beyond the
            already-public coverage_zips set + GeoJSON polygons. */}
        {!stillLoading && !bootstrapError && coverage && (
          <div className="mt-6" style={{ maxWidth: 560 }}>
            {/* Phase L.2 P2 — audit M-3. Visible + programmatic pointer
                to the keyboard alternative for the Mapbox canvas.
                The map's `aria-describedby` on the canvas
                (`role="application"`) references this element's id
                so SR users hear the keyboard hint immediately on
                entering the map region. */}
            <p
              id="map-keyboard-alt-hint"
              className="pp-sm mb-3"
              style={{ color: "var(--pp-text-soft)" }}
              data-testid="map-keyboard-alt-hint"
            >
              <Sparkles
                size={14}
                style={{ color: "var(--pp-orange)", display: "inline-block", marginRight: 6 }}
                aria-hidden
              />
              Use the ZIP search below to check coverage by keyboard.
            </p>
            <ZipSearchBox
              coveredZips={coverage?.coverage_zips || []}
              geojson={geojson}
              onMatch={(z) => setActiveZip(z)}
            />
            {/* Coverage-Map multi-metro readiness — dynamic metro
                quick-jump chip row. Chips are derived from the
                /public/metros endpoint; new metros in the DB render
                new chips with zero code change. */}
            <MetroChipRow mapRef={mapRef} />
            {/* F.3 — admin-only density overlay. The component itself
                returns null when isAdmin === false, so the public
                visitor never sees the toggle or any of the data. */}
            <AdminDensityOverlay
              onDensityChange={setDensity}
              onZipFocus={(z) => setActiveZip(z)}
            />
            {/* Phase L.2 P2 — audit M-7. Polite region announces
                served-ZIPs count changes (metro switch, density
                filter). Region stays mounted so the transitions
                fire correctly. */}
            <PoliteStatus
              visuallyHidden
              message={
                coverage?.coverage_zips
                  ? `${coverage.coverage_zips.length} ZIP${coverage.coverage_zips.length === 1 ? "" : "s"} currently covered.`
                  : ""
              }
              testId="map-served-count-status"
            />
          </div>
        )}
      </section>

      {/* MAP + SIDE PANEL, or EMPTY STATE */}
      <section className="pp-wrap" style={{ paddingBottom: 48 }}>
        {bootstrapError ? (
          <div
            className="rounded-[24px] p-6"
            style={{
              border: "1px solid var(--pp-line)",
              background: "var(--pp-v3-surface)",
            }}
            data-testid="map-bootstrap-error"
          >
            <p className="pp-sm text-red-700">
              {bootstrapError}
            </p>
          </div>
        ) : stillLoading ? (
          <div
            className="rounded-[24px] p-10 text-center"
            style={{
              minHeight: "60vh",
              background: "var(--pp-v3-surface)",
              border: "1px solid var(--pp-line)",
            }}
            data-testid="map-loading"
            role="status"
            aria-live="polite"
          >
            <p className="pp-sm" style={{ color: "var(--pp-text-soft)" }}>
              Loading coverage…
            </p>
          </div>
        ) : hasCoverage ? (
          <div className="grid lg:grid-cols-[1fr_360px] gap-6">
            <MapCanvas
              servedZips={coverage?.coverage_zips || []}
              geojson={geojson}
              coverage={coverage}
              mapRef={mapRef}
              onZipClick={handleZipClick}
              density={density}
            />
            {/* Desktop side panel — hidden on small screens; the F.5
                MapBottomSheet handles mobile UX instead. */}
            <div className="hidden lg:flex flex-col gap-4">
              <ZipSidePanel
                activeZip={activeZip}
                providers={providers}
                loading={providersLoading}
                error={providersError}
                onClose={() => setActiveZip(null)}
              />
              {/* Pre-Launch Demand Capture (2026-07-21) — waitlist
                  demand overlay. Aggregate ZIP + metro counts sourced
                  from /public/waitlist/demand with small-count
                  suppression (Guard 15). Never renders individual
                  pins or PII. Graceful in the token-missing fallback
                  path — the sidebar column remains visible even when
                  the Mapbox canvas is a placeholder. */}
              <WaitlistDemandOverlay />
            </div>
          </div>
        ) : (
          <EmptyStatePanel />
        )}
      </section>

      {/* F.5 — Mobile bottom sheet. `lg:hidden` inside the component
          keeps it off desktop. Renders even when activeZip is null
          (just stays in the "closed" state); the open transition
          fires when activeZip becomes non-null. */}
      {hasCoverage && (
        <MapBottomSheet
          activeZip={activeZip}
          providers={providers}
          loading={providersLoading}
          error={providersError}
          onClose={() => setActiveZip(null)}
          coveredZips={coverage?.coverage_zips || []}
          onSelectZip={(z) => setActiveZip(z)}
        />
      )}

      {/* STATS BAND — only rendered when we have coverage. */}
      {hasCoverage && (
        <section
          className="pp-wrap"
          style={{ paddingTop: 8, paddingBottom: 48 }}
          data-testid="map-stats-band"
        >
          <div
            className="rounded-[24px] p-8"
            style={{
              background: "var(--pp-lavender)",
              color: "var(--pp-lavender-text)",
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center md:text-left">
              <div>
                <div className="pp-kick">ZIPs served</div>
                <div
                  style={{
                    fontSize: 36,
                    fontWeight: 800,
                    letterSpacing: "-0.02em",
                  }}
                  data-testid="map-stat-zips"
                >
                  {totalZips}
                </div>
              </div>
              <div>
                <div className="pp-kick">Cities</div>
                <div
                  style={{
                    fontSize: 36,
                    fontWeight: 800,
                    letterSpacing: "-0.02em",
                  }}
                  data-testid="map-stat-cities"
                >
                  {totalCities}
                </div>
              </div>
              <div>
                <div className="pp-kick">Service area</div>
                <div
                  style={{ fontSize: 18, fontWeight: 700, marginTop: 6 }}
                  data-testid="map-stat-area"
                >
                  {coverage?.cities?.join(" · ") || "Your area"}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA BAND — always rendered. */}
      <section
        className="pp-wrap"
        style={{ paddingBottom: 96 }}
        data-testid="map-cta-band"
      >
        <div
          className="rounded-[28px] p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
          style={{
            background: "var(--pp-purple)",
            color: "#fff",
            boxShadow: "var(--pp-shadow)",
          }}
        >
          <div style={{ maxWidth: "28em" }}>
            <div className="pp-kick" style={{ color: "rgba(255,255,255,0.8)" }}>
              Ready to Pluck?
            </div>
            <h2 className="pp-h2 mt-2" style={{ color: "#fff", fontSize: 28 }}>
              {hasCoverage
                ? "Book your first cleanup or join the PooPartner crew."
                : "Help us launch faster — join the PooPartner crew."}
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            {hasCoverage && (
              <Link
                to="/book"
                className="pp-btn pp-btn-primary"
                data-testid="map-cta-book"
              >
                Book a cleanup
                <ArrowRight className="arrow" size={14} aria-hidden />
              </Link>
            )}
            <Link
              to="/signup?role=provider"
              className="pp-btn pp-btn-ghost"
              style={{
                background: "transparent",
                color: "#fff",
                borderColor: "rgba(255,255,255,0.5)",
              }}
              data-testid="map-cta-partner"
            >
              Become a PooPartner
            </Link>
          </div>
        </div>
      </section>
      <WinnerSlotCard winnerRank={10} />
    </PublicPageShell>
  );
}
